import React, { useCallback, useRef, useState } from "react";
import { MoveHorizontal } from "lucide-react";

export default function BeforeAfterSlider({ before, after, dark = false }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const update = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(100, Math.max(0, pct)));
  }, []);

  const onDown = (e) => {
    dragging.current = true;
    update(e.clientX);
  };
  const onMove = (e) => {
    if (dragging.current) update(e.clientX);
  };
  const stop = () => (dragging.current = false);

  return (
    <div>
      <div
        ref={ref}
        data-testid="before-after-slider"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={stop}
        onPointerLeave={stop}
        className="relative aspect-[4/3] w-full touch-none select-none overflow-hidden bg-slate-100"
        role="slider"
        aria-valuenow={Math.round(pos)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Before and after comparison"
      >
        {/* After (base) */}
        <img src={after.src} alt={after.caption} className="pointer-events-none absolute inset-0 h-full w-full object-cover" draggable="false" />
        <span className="absolute right-3 top-3 bg-[#1D4ED8] px-2 py-1 font-display text-[10px] uppercase tracking-widest text-white">After</span>

        {/* Before (clipped overlay) */}
        <img
          src={before.src}
          alt={before.caption}
          draggable="false"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        />
        <span
          className="absolute left-3 top-3 bg-[#0A1128] px-2 py-1 font-display text-[10px] uppercase tracking-widest text-white"
          style={{ opacity: pos > 12 ? 1 : 0 }}
        >
          Before
        </span>

        {/* Handle */}
        <div className="pointer-events-none absolute inset-y-0" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
          <div className="relative h-full w-0.5 bg-white shadow-[0_0_0_1px_rgba(10,17,40,0.25)]">
            <div className="absolute top-1/2 left-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#0A1128] shadow-lg">
              <MoveHorizontal size={20} />
            </div>
          </div>
        </div>
      </div>
      <div className={`mt-3 flex items-center justify-between text-xs ${dark ? "text-slate-300" : "text-slate-500"}`}>
        <span>{before.caption}</span>
        <span className={`font-display uppercase tracking-widest ${dark ? "text-slate-400" : "text-slate-400"}`}>Drag to compare</span>
        <span className="text-right">{after.caption}</span>
      </div>
    </div>
  );
}
